import { createSchemaUtils, FormValidation, RJSFValidationError, UiSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { JSONSchema7Object } from 'json-schema';
import { get } from 'lodash';
import pluralize from 'pluralize';

import {
  generateRjsfSchemaAndErrors,
  transformErrorProperty,
} from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/utils';
import { getHandlebars, isValidHandlebarsText } from 'dashboard/lib/utils/handlebars';
import {
  fieldPathHasValidSchema,
  WorkflowValidationContext,
} from 'dashboard/lib/workflows/validation/validationContext';
import { ExternalIntegrationVendor } from 'dashboard/models/external-integration-vendor';
import {
  WorkflowActionMeta,
  WorkflowConfigError,
  WorkflowErrorType,
} from 'dashboard/models/workflow';
import { GenericWorkflowAction, UiSchemaWidgetTypes } from 'dashboard/models/workflow-schema';
import {
  NullWorkflowStepActionKey,
  WorkflowActionFromConfig,
  WorkflowActionRunGraphQueryV1,
  WorkflowActionRunHttpPlatformIntegrationV1,
  WorkflowStepAction,
  WorkflowStepActionKey,
  WorkflowStepType,
} from 'dashboard/models/workflow-version';
import { findPath } from 'lib/workflows';

import { UsableObjectConfig } from '../objects';

const generateRjsfSchemaFor = (
  step: WorkflowStepAction,
  genericAction: GenericWorkflowAction,
  usableObjects: UsableObjectConfig[],
  actionMeta: WorkflowActionMeta,
  externalIntegrationVendors: ExternalIntegrationVendor[],
) => {
  const formData: JSONSchema7Object | null =
    step.action?.key === WorkflowStepActionKey.RunHttpPlatformIntegration
      ? (step.action as WorkflowActionRunHttpPlatformIntegrationV1).params.request_body
      : (step.action as WorkflowActionFromConfig).params;

  let schema = genericAction.request.schema;
  if (step.action?.key === WorkflowStepActionKey.RunHttpPlatformIntegration) {
    const reusableSchema = externalIntegrationVendors.find(
      (vendor) =>
        vendor.vendor ===
        (step.action as WorkflowActionRunHttpPlatformIntegrationV1).params.integration.vendor,
    );
    schema = {
      ...schema,
      ...reusableSchema?.['reusable-elements'],
    };
  }

  return generateRjsfSchemaAndErrors({
    schema,
    uiSchema: genericAction.request.uiSchema,
    usableObjects,
    actionParameters: formData || {},
    actionMeta,
  });
};

export const validateGenericActionStep = (
  step: WorkflowStepAction,
  genericAction: GenericWorkflowAction,
  validationContext: WorkflowValidationContext,
  externalIntegrationVendors: ExternalIntegrationVendor[],
): WorkflowConfigError[] => {
  if (step.action.key === NullWorkflowStepActionKey.NullKey) {
    return [
      {
        token: step.token,
        message: 'This action is invalid',
        type: WorkflowErrorType.Save,
      },
    ];
  }

  const stepContext = validationContext.getContextForStep(step.token);
  const { schema: generatedSchema, errorSchema } = generateRjsfSchemaFor(
    step,
    genericAction,
    stepContext.usableObjects,
    validationContext.actionMeta,
    externalIntegrationVendors,
  );

  const platformIntegrationCredentialError: WorkflowConfigError[] =
    step.action.key === WorkflowStepActionKey.RunHttpPlatformIntegration &&
    step.action.params.integration_credential == null
      ? [
          {
            token: step.token,
            field: 'integration-config',
            message: 'Please set a valid Integration Credential',
            type: WorkflowErrorType.Save,
          },
        ]
      : [];

  const formData: JSONSchema7Object | null =
    step.action.key === WorkflowStepActionKey.RunHttpPlatformIntegration
      ? (step.action as WorkflowActionRunHttpPlatformIntegrationV1).params.request_body
      : (step.action as WorkflowActionFromConfig).params;

  const customValidate = (
    formData: JSONSchema7Object,
    errors: FormValidation,
    uiSchema?: UiSchema,
  ): FormValidation => {
    // handlebars validation
    for (const key in uiSchema) {
      if (uiSchema[key]['ui:field'] === 'handlebars') {
        const handlebarsText = formData[key] as string;
        if (!isValidHandlebarsText(handlebarsText)) {
          (errors[key] as FormValidation).addError(
            `${uiSchema[key]['ui:title']} is invalid Handlebars syntax`,
          );
          continue;
        }

        const invalidPaths = getHandlebars(handlebarsText).filter(
          (handlebar) => !fieldPathHasValidSchema(handlebar, stepContext, validationContext).valid,
        );
        if (invalidPaths.length > 0) {
          (errors[key] as FormValidation).addError(
            `${uiSchema[key]['ui:title']} has invalid Handlebar references: ${invalidPaths.join(
              ', ',
            )}`,
          );
        }
      }
    }

    // custom validation for attach_objects_to_case action
    if (step.action.key === WorkflowStepActionKey.AttachObjectsToCase) {
      // when passed a graphQuery case object created from a runGraphQuery workflow step,
      // ensure that a snapshot is being generated as well
      const stepsInPath = findPath(step.token, validationContext.stepsConfig);
      const runGraphQuerySteps: WorkflowStepAction[] = stepsInPath.filter(
        (stepInPath) =>
          stepInPath.type === WorkflowStepType.Action &&
          stepInPath.action?.key === WorkflowStepActionKey.RunGraphQuery,
      ) as WorkflowStepAction[];
      const runGraphQueryStepsAttachedToCase = runGraphQuerySteps.filter((step) =>
        ((formData['case-objects'] as string[]) || []).includes(step.token),
      );
      const stepsWithoutSnapshot = runGraphQueryStepsAttachedToCase
        .filter(
          (step) => !(step.action as WorkflowActionRunGraphQueryV1)?.params['generate-snapshot'],
        )
        .map((step) => `'${step.name}'`);

      if (stepsWithoutSnapshot.length > 0) {
        (errors['case-objects'] as FormValidation).addError(
          `Case objects contains graph query that does not generate a snapshot: ${stepsWithoutSnapshot.join(
            ', ',
          )}`,
        );
      }
    }

    return errors;
  };

  const transformErrors = (
    errors: RJSFValidationError[],
    uiSchema?: UiSchema,
  ): RJSFValidationError[] => {
    return (
      errors
        // (evan.qi) const errors are duplicated by oneOf errors, so filter them out
        .filter((error) => error.name !== 'const')
        .map((error) => {
          if (
            error.property &&
            step.action.key !== WorkflowStepActionKey.RunHttpPlatformIntegration
          ) {
            error.property = transformErrorProperty(error.property);
          }

          if (error.name === 'required') {
            if (
              error.property &&
              uiSchema &&
              get(uiSchema, [error.property, 'ui:widget']) === UiSchemaWidgetTypes.Select
            ) {
              error.message = 'must be selected';
            } else {
              error.message = 'must not be empty';
            }
          }
          if (error.name === 'oneOf') {
            error.message = 'must be one of the specified options';
          }
          if (error.name === 'minItems' && error.message) {
            const numItems = parseInt(error.message.replace(/\D/g, ''));
            error.message = `must have at least ${numItems} ${pluralize(
              'option',
              numItems,
            )} selected`;
          }
          if (error.name === 'minLength' && error.message) {
            const numChars = parseInt(error.message.replace(/\D/g, ''));
            error.message = `must have at least ${numChars} ${pluralize('character', numChars)}`;
          }
          if (error.property && uiSchema) {
            error.message = [
              get(uiSchema, [error.property, 'ui:title'], error.property),
              error.message,
            ].join(' ');
            error.stack = error.message;
          }
          return error;
        })
    );
  };

  let reusableSchema: ExternalIntegrationVendor | undefined;
  if (step.action.key === WorkflowStepActionKey.RunHttpPlatformIntegration) {
    const currentVendor = (step.action as WorkflowActionRunHttpPlatformIntegrationV1).params
      .integration.vendor;
    reusableSchema = externalIntegrationVendors.find((vendor) => vendor.vendor === currentVendor);
  }

  if (reusableSchema != null && reusableSchema?.['reusable-elements'] == null) {
    return [];
  }

  const schemaUtils = createSchemaUtils(validator, generatedSchema);

  let retrievedSchema;
  try {
    retrievedSchema = schemaUtils.retrieveSchema(generatedSchema, formData || {});
  } catch (error) {
    return [
      {
        type: WorkflowErrorType.Publish,
        token: step.token,
        field: 'schema',
        message: `Error retrieving schema: ${(error as Error).message}`,
      },
    ];
  }

  return [
    ...Object.keys(errorSchema).map(
      (property): WorkflowConfigError => ({
        type: WorkflowErrorType.Publish,
        token: step.token,
        field: property,
        message: errorSchema[property]?.__errors?.[0] || 'error',
      }),
    ),
    ...platformIntegrationCredentialError,
    ...validator
      .validateFormData(
        formData || {},
        retrievedSchema,
        customValidate,
        transformErrors,
        genericAction.request.uiSchema,
      )
      .errors.map(
        (error): WorkflowConfigError => ({
          type: WorkflowErrorType.Publish,
          token: step.token,
          field: transformErrorProperty(error.property),
          message: error.message || 'error',
        }),
      ),
  ];
};
